import type {
  IntakeConditionCode,
  IntakeRelation,
  IntakeLineageSide as VocabLineageSide,
  IntakeWorkflowStatus as VocabWorkflowStatus,
  PatternFlagAssessment,
} from './intakeVocabulary';

export type IntakeWorkflowStatus = VocabWorkflowStatus;

export type IntakeYesNoUnknown = 'yes' | 'no' | 'unknown';
export type IntakeConditionResponse = 'self' | 'family' | 'both' | 'neither';
export type IntakeLineageSide = VocabLineageSide;

/**
 * Patient-facing relative relation. Re-exports the canonical vocabulary so
 * the patient form writes the same codes the decision engine expects.
 * Legacy coarse values (`grandmother`, `aunt`, `cousin`, `child`) are kept
 * in the union for read-back compatibility with pre-existing documents.
 */
export type IntakeRelativeRelation = IntakeRelation;

export interface IntakeAddress {
  line1: string;
  line2?: string;
  city: string;
  postcode: string;
  country: string;
}

export interface IntakePersonalInfo {
  /** Legacy full-name field — preferred for display; auto-derived from first/last. */
  displayName: string;
  /** Structured names. Optional so existing records still parse. */
  firstName?: string;
  lastName?: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  /** Biological sex at birth. Restricted in the UI to `female` | `male` so
   * sex-specific screening rules can run without ambiguity. */
  sexAtBirth: string;
  address: IntakeAddress;
}

export interface IntakeContext {
  occupation: string;
  educationLevel: string;
  householdContext: string;
  financialConstraints: string;
  supportNetworkNotes: string;
}

/**
 * Drinking, asked in DRINKS rather than units.
 *
 * The clinicians testing intake asked for wording a member can answer without
 * arithmetic: nobody knows what a unit is, and `alcoholUse` was one free-text
 * box with an "e.g. 4 units/week" placeholder that invited a made-up number.
 *
 * The values mirror the ops interview's LifestyleHistory exactly, so the two
 * surfaces ask the same question and an answer given here can seed the
 * clinician's copy of it rather than being asked again.
 *
 * These deliberately do NOT produce a score, and are not an AUDIT-C result —
 * see docs/clinical/INTAKE_QUESTIONS_SIGNOFF.md block 3. They are stored
 * separately rather than as one sentence so a validated score could be derived
 * later without re-interviewing anyone.
 */
export type IntakeDrinkFrequency = '' | 'never' | 'monthly_or_less' | 'two_to_four_monthly' | 'two_to_three_weekly' | 'four_plus_weekly';
export type IntakeDrinksPerSession = '' | 'one_to_two' | 'three_to_four' | 'five_to_six' | 'seven_to_nine' | 'ten_plus';
export type IntakeHeavySessionFrequency = '' | 'never' | 'less_than_monthly' | 'monthly' | 'weekly' | 'daily_or_almost';
/** 'declined' is a real answer: a member who will not say otherwise says "no". */
export type IntakeDrugUse = '' | 'none' | 'occasional' | 'regular' | 'declined';

export interface IntakeLifestyle {
  smokingStatus: string;
  /**
   * Free text, kept. Superseded by the structured drink fields below, but not
   * removed — members have already answered it and dropping the field would
   * discard what they wrote.
   */
  alcoholUse: string;
  drinkFrequency?: IntakeDrinkFrequency;
  drinksPerSession?: IntakeDrinksPerSession;
  heavySessionFrequency?: IntakeHeavySessionFrequency;
  drinkType?: string;
  drugUse?: IntakeDrugUse;
  drugDetail?: string;
  exercisePattern: string;
  sleepPattern: string;
  dietPattern: string;
  stressLevel: string;
  exposuresNotes: string;
}

export interface IntakeVaccinationHistory {
  childhoodVaccinationsUpToDate: IntakeYesNoUnknown;
  covidVaccinationStatus: string;
  travelVaccinationsNotes: string;
  otherVaccinationNotes: string;
}

export interface IntakeMedicalTimelineEntry {
  id: string;
  label: string;
  dateLabel: string;
  notes: string;
}

/* =====================================================================
 * Personal history — meds, allergies, active diagnoses, prior genetic
 * testing and ancestry. Previously these lived as free text in
 * `patientNotes` / `exposuresNotes`; capturing them structured means the
 * decision engine can reason over them and the clinician can see them as
 * a tidy list during the intake interview.
 * ===================================================================== */

export type IntakeAllergySeverity = 'mild' | 'moderate' | 'severe' | 'unknown';

export type IntakeMedicationDoseUnit =
  | 'mg'
  | 'mcg'
  | 'g'
  | 'ml'
  | 'units'
  | 'puffs'
  | 'tablets'
  | 'drops'
  | 'patch'
  | 'other';

export type IntakeMedicationFrequencyPeriod = 'day' | 'week' | 'month' | 'prn';

export interface IntakePersonalMedication {
  id: string;
  name: string;
  /** Structured dose (preferred). `doseLabel` is kept for back-compat and display. */
  doseAmount?: number | null;
  doseUnit?: IntakeMedicationDoseUnit;
  doseLabel?: string;
  /** Structured frequency (preferred). `frequencyLabel` is kept for back-compat. */
  frequencyTimes?: number | null;
  frequencyPeriod?: IntakeMedicationFrequencyPeriod;
  frequencyLabel?: string;
  reason?: string;
}

export interface IntakePersonalAllergy {
  id: string;
  allergen: string;
  reactionLabel?: string;
  severity?: IntakeAllergySeverity;
}

export interface IntakePersonalDiagnosis {
  id: string;
  /** Canonical condition code when the patient's entry maps to one. */
  code?: IntakeConditionCode;
  /** Free-text label as typed. Always filled for display. */
  label: string;
  diagnosedYear?: number | null;
  ageAtDiagnosis?: number | null;
  notes?: string;
}

export interface IntakePriorGeneticTest {
  id: string;
  testName: string;
  yearLabel?: string;
  providerLabel?: string;
  resultSummary?: string;
  /** Free-text list of known pathogenic variants, if any. */
  knownVariants?: string;
}

/* =====================================================================
 * Ancestry — structured so the decision engine / clinician can reason
 * over Ashkenazi BRCA risk, European APOE bases, South Asian CVD baselines
 * etc. without parsing free text.
 * ===================================================================== */

export type IntakeAncestryRegion =
  | 'european_northern'
  | 'european_southern'
  | 'european_eastern'
  | 'ashkenazi_jewish'
  | 'sephardi_jewish'
  | 'mizrahi_jewish'
  | 'sub_saharan_african'
  | 'north_african'
  | 'middle_eastern'
  | 'south_asian'
  | 'east_asian'
  | 'southeast_asian'
  | 'central_asian'
  | 'pacific_islander'
  | 'native_american'
  | 'hispanic_latino'
  | 'caribbean'
  | 'other';

export type IntakeAncestryLineage = 'maternal' | 'paternal' | 'both' | 'unknown';

export interface IntakeAncestryEntry {
  id: string;
  region: IntakeAncestryRegion;
  lineage?: IntakeAncestryLineage;
  detail?: string;
}

export interface IntakePersonalHistory {
  medications: IntakePersonalMedication[];
  allergies: IntakePersonalAllergy[];
  activeDiagnoses: IntakePersonalDiagnosis[];
  priorGeneticTests: IntakePriorGeneticTest[];
  /** Structured ancestry entries (preferred). */
  ancestries?: IntakeAncestryEntry[];
  /** Free-text ancestry notes (kept as supplementary context). */
  ancestryNotes?: string;
}

export const ANCESTRY_CATALOG: Record<IntakeAncestryRegion, { label: string; hint?: string }> = {
  european_northern: { label: 'Northern / Western European', hint: 'e.g. British, Irish, French, German, Dutch, Scandinavian' },
  european_southern: { label: 'Southern European', hint: 'e.g. Italian, Spanish, Portuguese, Greek' },
  european_eastern: { label: 'Eastern European', hint: 'e.g. Polish, Russian, Ukrainian, Balkan' },
  ashkenazi_jewish: { label: 'Ashkenazi Jewish', hint: 'Central / Eastern European Jewish heritage' },
  sephardi_jewish: { label: 'Sephardi Jewish', hint: 'Iberian Peninsula / Mediterranean Jewish heritage' },
  mizrahi_jewish: { label: 'Mizrahi Jewish', hint: 'Middle Eastern / North African Jewish heritage' },
  sub_saharan_african: { label: 'Sub-Saharan African' },
  north_african: { label: 'North African' },
  middle_eastern: { label: 'Middle Eastern' },
  south_asian: { label: 'South Asian', hint: 'e.g. Indian, Pakistani, Bangladeshi, Sri Lankan' },
  east_asian: { label: 'East Asian', hint: 'e.g. Chinese, Japanese, Korean' },
  southeast_asian: { label: 'Southeast Asian', hint: 'e.g. Vietnamese, Thai, Filipino, Malay' },
  central_asian: { label: 'Central Asian' },
  pacific_islander: { label: 'Pacific Islander' },
  native_american: { label: 'Native American / Indigenous' },
  hispanic_latino: { label: 'Hispanic / Latino' },
  caribbean: { label: 'Caribbean' },
  other: { label: 'Other / mixed' },
};

export interface IntakeFamilyStructure {
  siblingsCount: number | null;
  siblingsHaveChildren: IntakeYesNoUnknown;
  maternalAuntsCount: number | null;
  maternalUnclesCount: number | null;
  paternalAuntsCount: number | null;
  paternalUnclesCount: number | null;
  knewAllGrandparents: IntakeYesNoUnknown;
  childrenCount: number | null;
  notes: string;
}

export interface IntakeFamilyConditionAssignment {
  id: string;
  /** Short slug for UI grouping (e.g. `heart_disease`). */
  conditionKey: string;
  /** Canonical engine code, when mapped. */
  code?: IntakeConditionCode;
  conditionLabel: string;
  response: IntakeConditionResponse;
  familyMemberIds: string[];
  selfNotes: string;
  familyNotes: string;
  /** Per-relative details for this condition, keyed by relative id. */
  relativeDetails?: IntakeFamilyConditionRelativeDetail[];
}

export interface IntakeFamilyConditionRelativeDetail {
  relativeId: string;
  ageAtDiagnosis?: number | null;
  notes?: string;
}

export interface IntakeRelativeCondition {
  id: string;
  /** Canonical engine code when the condition maps to one. */
  code?: IntakeConditionCode;
  label: string;
  ageAtDiagnosis?: number | null;
  notes?: string;
}

export interface IntakeRelativeRecord {
  id: string;
  relation: IntakeRelativeRelation;
  relationLabel: string;
  relationOtherDetails?: string;
  lineageSide: IntakeLineageSide;
  bloodRelated: boolean;
  connectedToRelativeId?: string;
  sex: 'female' | 'male' | 'unknown';
  aliveStatus: 'alive' | 'deceased' | 'unknown';
  currentAge?: number | null;
  ageAtDeath?: number | null;
  causeOfDeath?: string;
  priorGeneticTesting: IntakeYesNoUnknown;
  conditions: IntakeRelativeCondition[];
  notes?: string;
}

export interface PatientFamilyHistoryIntakeRecord {
  id?: string;
  userId: string;
  status: IntakeWorkflowStatus;
  currentVersion: number;
  personalInfo: IntakePersonalInfo;
  context: IntakeContext;
  lifestyle: IntakeLifestyle;
  vaccinationHistory: IntakeVaccinationHistory;
  personalHistory?: IntakePersonalHistory;
  surgeries: IntakeMedicalTimelineEntry[];
  hospitalisations: IntakeMedicalTimelineEntry[];
  traumaHistory: IntakeMedicalTimelineEntry[];
  familyStructure: IntakeFamilyStructure;
  relatives: IntakeRelativeRecord[];
  conditionAssignments: IntakeFamilyConditionAssignment[];
  /**
   * Auto-suggested / clinician-confirmed pattern flags for the decision
   * engine. Optional because historic records don't have this field.
   */
  patternFlags?: PatternFlagAssessment[];
  /** Free-text note the patient can leave for the care clinician. */
  readinessNotes?: string;
  patientNotes: string;
  opsClarificationMessage?: string;
  latestSubmittedAt?: any;
  firstSubmittedAt?: any;
  lockedAt?: any;
  lockedBy?: string | null;
  doctorReviewStartedAt?: any;
  doctorReviewedAt?: any;
  opsSignedOffAt?: any;
  publishedFamilyTreeId?: string | null;
  lastEditableAt?: any;
  source?: 'patient' | 'legacy_migration';
  sourceType?: string | null;
  sourceSystem?: string | null;
  reviewState?: string | null;
  reviewedAt?: any;
  reviewedByUid?: string | null;
  reviewedByRole?: string | null;
  assignedSeniorClinicianUid?: string | null;
  assignedSeniorClinicianName?: string | null;
  /** @deprecated retired care-clinician role — legacy docs only. */
  assignedCareClinicianUid?: string | null;
  /** @deprecated retired care-clinician role — legacy docs only. */
  assignedCareClinicianName?: string | null;
  assignedMedicalDirectorUid?: string | null;
  assignedMedicalDirectorName?: string | null;
  createdAt?: any;
  updatedAt?: any;
  schemaVersion?: number;
  modelVersion?: string | null;
}

export type ChangeRequestStatus = 'open' | 'triaged' | 'approved' | 'implemented' | 'rejected';

export interface FamilyHistoryChangeRequestRecord {
  id?: string;
  userId: string;
  intakeId?: string | null;
  workflowId?: string | null;
  familyTreeId?: string | null;
  requestedArea: 'family_tree' | 'condition_history' | 'personal_history' | 'other';
  message: string;
  proposedCorrection?: string;
  status: ChangeRequestStatus;
  submittedAt?: any;
  triagedAt?: any;
  triagedBy?: string | null;
  resolutionNotes?: string;
  sourceType?: string | null;
  sourceSystem?: string | null;
  reviewState?: string | null;
  reviewedAt?: any;
  reviewedByUid?: string | null;
  reviewedByRole?: string | null;
  createdAt?: any;
  updatedAt?: any;
  schemaVersion?: number;
  modelVersion?: string | null;
}

/**
 * Patient-facing condition cards. Each card maps 1:1 onto a decision-engine
 * `IntakeConditionCode` so the patient's answers feed the engine directly
 * instead of needing ops to translate from free-text labels.
 *
 * Kept as a list (not `CONDITION_CATALOG`) because the patient sees fewer
 * codes than the clinician: we bundle related codes (e.g. MI + CAD + Stroke
 * → "Heart disease") and hide rarer ones that add cognitive load without
 * changing rule outcomes. Pattern flags are computed from the full code set
 * later in the workflow.
 */
export const CORE_CONDITION_OPTIONS: Array<{ key: string; label: string; code: IntakeConditionCode }> = [
  { key: 'heart_disease', label: 'Heart disease, heart attack or stroke', code: 'MI' },
  { key: 'sudden_cardiac_death', label: 'Sudden cardiac death in a relative', code: 'SUDDEN_CARDIAC_DEATH' },
  { key: 'high_cholesterol', label: 'High cholesterol', code: 'HYPERLIPIDAEMIA' },
  { key: 'hypertension', label: 'High blood pressure', code: 'HYPERTENSION' },
  { key: 'type_2_diabetes', label: 'Type 2 diabetes or prediabetes', code: 'T2D' },
  { key: 'type_1_diabetes', label: 'Type 1 diabetes', code: 'T1D' },
  { key: 'breast_cancer', label: 'Breast cancer', code: 'BREAST' },
  { key: 'ovarian_cancer', label: 'Ovarian cancer', code: 'OVARIAN' },
  { key: 'colorectal_cancer', label: 'Bowel / colorectal cancer', code: 'COLORECTAL' },
  { key: 'prostate_cancer', label: 'Prostate cancer', code: 'PROSTATE' },
  { key: 'pancreatic_cancer', label: 'Pancreatic cancer', code: 'PANCREATIC' },
  { key: 'melanoma', label: 'Melanoma (skin cancer)', code: 'MELANOMA' },
  { key: 'endometrial_cancer', label: 'Endometrial (uterine) cancer', code: 'ENDOMETRIAL' },
  { key: 'other_cancer', label: 'Other cancer', code: 'OTHER_CANCER' },
  { key: 'dementia', label: 'Dementia or Alzheimer\u2019s', code: 'DEMENTIA' },
  { key: 'parkinson', label: 'Parkinson\u2019s disease', code: 'PARKINSON' },
  { key: 'vte', label: 'Blood clot (DVT or pulmonary embolism)', code: 'VTE' },
  { key: 'autoimmune', label: 'Autoimmune disease (lupus, RA, coeliac\u2026)', code: 'LUPUS' },
];

export type {
  IntakeConditionCode,
  IntakeRelation,
  PatternFlagAssessment,
} from './intakeVocabulary';
